import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import clsx from 'clsx'
import { appApiUrls, appPageUrls } from '@shared/url-map.js'
import { POSTHOG_PROJECT_URL } from '@shared/constants.js'
import type { ClientPurchaseSignals } from '@shared/common-types.js'

type LoadState = { kind: 'loading' } | { kind: 'requestFailed' } | { kind: 'loaded'; signals: ClientPurchaseSignals }

function useClientPurchaseSignals(): LoadState {
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  useEffect(() => {
    const controller = new AbortController()
    axios
      .get(appApiUrls.revenueCatClientSignals, { signal: controller.signal })
      .then((res) => {
        setState(res.data.code === 0 ? { kind: 'loaded', signals: res.data.data } : { kind: 'requestFailed' })
      })
      .catch((error) => {
        if (axios.isCancel(error)) return
        console.error('Failed to load client purchase signals', error)
        setState({ kind: 'requestFailed' })
      })
    return () => controller.abort()
  }, [])
  return state
}

function formatShanghaiClock(iso: string) {
  return new Date(iso).toLocaleTimeString('zh-CN', {
    timeZone: 'Asia/Shanghai',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function Row({ label, alert, children }: { label: string; alert: boolean; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-3">
      <span className="w-36 shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
      <span className={clsx(alert && 'font-bold text-red-500')}>{children}</span>
    </div>
  )
}

function SignalRows({ signals }: { signals: ClientPurchaseSignals }) {
  if (signals.state === 'unconfigured') {
    return <span className="text-gray-500 dark:text-gray-400">未配置 POSTHOG_PERSONAL_API_KEY</span>
  }
  if (signals.state === 'failed') {
    return <span className="text-red-400">PostHog 查询失败：{signals.reason}</span>
  }
  const failureEvents = signals.purchaseFailures.reduce((sum, row) => sum + row.events, 0)
  return (
    <>
      <Row label="购买失败（按原因）" alert={failureEvents > 0}>
        {failureEvents === 0
          ? '0'
          : signals.purchaseFailures.map((row) => (
              <span key={row.code} className="mr-3">
                {row.code || '（无原因码）'} {row.events} 次 / {row.people} 人
              </span>
            ))}
      </Row>
      <Row label="连不上服务器的人" alert={signals.cannotConnectPeople > 0}>
        -1004 连不上 {signals.cannotConnectPeople} 人
        <span className="ml-3 font-normal text-gray-500 dark:text-gray-400">-1001 超时 {signals.timeoutPeople} 人</span>
      </Row>
      <Row label="代理不通时成交" alert={false}>
        {signals.rcSyncPendingPurchases}
      </Row>
      <span className="text-xs text-gray-400">{formatShanghaiClock(signals.fetchedAt)} 取数，缓存 10 分钟</span>
    </>
  )
}

/**
 * 今天（北京时间）客户端埋点里的购买和连接信号。这页每天都会看，所以放在顶上。
 *
 * 只看服务端日志不够：连不上我们服务器的请求、StoreKit 自己报的错，服务器上一条都没有，
 * 2026-10-22 那天服务端看起来全绿，实际有人买不了。所以这里的数取自 PostHog，
 * 服务端代理那一侧的明细在 /admin/revenuecat。
 */
export default function ClientPurchaseSignalsCard() {
  const state = useClientPurchaseSignals()
  const alert =
    state.kind === 'loaded' &&
    state.signals.state === 'ok' &&
    (state.signals.purchaseFailures.some((row) => row.events > 0) || state.signals.cannotConnectPeople > 0)

  return (
    <div
      className={clsx(
        'flex flex-col gap-1 rounded-xl border bg-white px-4 py-2 text-sm dark:bg-slate-700',
        alert ? 'border-red-400' : 'border-transparent',
      )}
    >
      <div className="flex flex-wrap items-center gap-4">
        <span className="font-medium">客户端购买信号（今日，北京时间）</span>
        <a className="text-blue-500" href={POSTHOG_PROJECT_URL} target="_blank" rel="noreferrer">
          在 PostHog 查看 ↗
        </a>
        <Link className="text-blue-500" to={appPageUrls.revenueCat}>
          服务端代理明细 →
        </Link>
      </div>
      {state.kind === 'loading' && <span className="text-gray-400">loading...</span>}
      {state.kind === 'requestFailed' && <span className="text-red-400">加载失败</span>}
      {state.kind === 'loaded' && <SignalRows signals={state.signals} />}
    </div>
  )
}
